<template>
  <div>
    <FilterListing
      v-if="isFilterListingShow"
      :return-to-title="entry.title"
      :page-profile="entry.pageProfile"
      @backBtbClick="hideFilterListing"
    />
    <div v-else>
      <ExercisesHeader :image="entry.image[0]">
        <template #boxDesktop>
          <h1 class="fluid-3xl leading-tightest">{{ entry.title }}</h1>
          <div
            v-if="entry.teaserText"
            class="my-4"
            :class="[
              entry.teaserText.length < 220
                ? 'fluid-base leading-relaxed font-light'
                : 'fluid-base leading-relaxed font-light line-clamp-6',
            ]"
            v-html="entry.teaserText"
          ></div>
          <button
            v-if="entry.filterLinkText"
            class="hover:underline exercise-header__button"
            @click="showFilterListing"
          >
            {{ entry.filterLinkText }}
            <InlineArrowIcon class="inline h-2" />
          </button>
        </template>
        <template #boxMobile>
          <h1 class="fluid-3xl leading-tightest">{{ entry.title }}</h1>
          <div class="my-4 fluid-base leading-relaxed font-light" v-html="entry.teaserText"></div>
          <button v-if="entry.filterLinkText" data-test-id="show-filter" @click="showFilterListing">
            {{ entry.filterLinkText }}
            <InlineArrowIcon class="inline h-2" />
          </button>
        </template>
      </ExercisesHeader>

      <ExercisesBlocks :entry="entry" />
    </div>
  </div>
</template>
<script>
import gql from 'graphql-tag';
import { isFiltersQuery, exercisesUrlQuery, prerefinementsByPageProfile } from '@/utils/algolia';
import ExercisesHeader from '@/components/exercises/ExercisesHeader';
import InlineArrowIcon from '@/components/icons/InlineArrowIcon';

import ExercisesBlocks, { ExercisesBlocksMatrixFragment } from '@/components/exercises/ExercisesBlocks';
const FilterListing = () =>
  import(/* webpackChunkName: "exercise-filter-listing" */ '@/components/exercises/FilterListing');

export const ExercisePageFragment = gql`
  fragment ExercisePageFragment on exercisesPage_Entry {
    image {
      kind
      url
      width
      height
    }
    filterLinkText
    teaserText
    products {
      ... on products_Entry {
        handle: shopifyHandle
        title
      }
    }
    bodyParts {
      ... on bodyParts_Category {
        title
      }
    }
    painPoints {
      ... on painPoints_Category {
        title
      }
    }
    sports {
      ... on sports_Category {
        title
      }
    }
    routineTopics {
      title
    }

    pageProfile
    seoTopics

    exercisesBlocks {
      ...ExercisesBlocksMatrixFragment
    }
  }
  ${ExercisesBlocksMatrixFragment}
`;
/**
 * renders landing or filter page.
 * state is stored in url-query part in algolia persistend url format
 * on mount prerefined entry.products are synced with shopify to match algolia title
 */
export default {
  components: {
    ExercisesHeader,
    FilterListing,
    ExercisesBlocks,
    InlineArrowIcon,
  },
  props: {
    entry: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return { filterPresets: {} };
  },
  async fetch() {
    await this.setFilterPrerefinements();
  },
  computed: {
    entryFilterPresets() {
      const presetKeys = [
        'products',
        'bodyParts',
        'painPoints',
        'sports',
        'routineTopics',
        'difficulty',
        'trainingGoals',
      ];
      return presetKeys.reduce((acc, curr) => {
        if (this.entry[curr]) acc[curr] = this.entry[curr];
        return acc;
      }, {});
    },
    filterRoute() {
      const refinementList = prerefinementsByPageProfile(this.filterPresets, this.entry.pageProfile);
      return { path: `?${exercisesUrlQuery(refinementList)}` };
    },
    isFilterListingShow() {
      return isFiltersQuery(this.$route.query);
    },
  },
  mounted() {
    // mount listeninng on external filterReset from navbar
    this.$root.$on('exercisesFilterToggle', () => {
      this.showFilterListing();
    });
  },
  methods: {
    async setFilterPrerefinements() {
      // get presets from craft entry
      this.filterPresets = this.entryFilterPresets;

      // overwrite products with shopify products
      try {
        const shopifyProducts = await this.fetchShopifyProducts();
        if (shopifyProducts && shopifyProducts.length) {
          this.filterPresets.products = shopifyProducts.map((p) => ({ handle: p.handle, title: p.title }));
        }
      } catch (e) {
        console.warn(e.message);
      }
    },
    /**
     * fetch prerefined products to receive algolia-agnostic products.title facet for prerefinement url
     */
    async fetchShopifyProducts() {
      const products = this.entry.products;
      if (!products || !products.length) return [];
      return await this.$shopifyProductsRequest(products.map((p) => p.handle));
    },

    /**
     * push route to algolia refined url format
     * only if filterListing currently not shown
     */
    showFilterListing() {
      if (!this.isFilterListingShow) this.$router.push(this.filterRoute);
    },
    /**
     * reset route, remove filter state from route
     */
    hideFilterListing() {
      // push only path without query params
      this.$router.push({ path: this.$route.path });
    },
  },
};
</script>
